import { AxiosError } from 'axios'
import { createStore } from './util'
import { useApp } from './app'

export type SocialPlatform = 'twitter' | 'discord' | 'telegram'

interface SocialRewardsStore {
  claimed: Record<SocialPlatform, boolean>
  loading: boolean
  error: string | null
  getClaims: () => Promise<void>
  claim: (platform: SocialPlatform) => Promise<{ awarded: boolean; totalPoints: number } | null>
  clearError: () => void
  getUrl: (platform: SocialPlatform) => string
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const responseMessage = (error.response?.data as { message?: string })?.message
    return responseMessage || error.message || 'Unexpected error'
  }
  return 'Unexpected error'
}

const SOCIAL_URLS: Record<SocialPlatform, string> = {
  twitter: 'https://twitter.com/DawnInternet',
  discord: 'https://discord.gg/dawninternet',
  telegram: 'https://t.me/DawnInternet',
}

export const useSocialRewards = createStore<SocialRewardsStore>(
  'social-rewards',
  (set, get) => ({
    claimed: { twitter: false, discord: false, telegram: false },
    loading: false,
    error: null,

    getClaims: async () => {
      const http = useApp.getState().http
      set({ loading: true, error: null })
      try {
        const res = await http.get<{ claimed: SocialPlatform[] }>('/social/claims')
        const claimedSet: Record<SocialPlatform, boolean> = {
          twitter: false,
          discord: false,
          telegram: false,
        }
        for (const p of res.data.claimed || []) {
          claimedSet[p] = true
        }
        set({ claimed: claimedSet })
      } catch (error) {
        const errorMsg = extractErrorMessage(error)
        set({ error: errorMsg })
        console.error(errorMsg)
      } finally {
        set({ loading: false })
      }
    },

    claim: async (platform: SocialPlatform) => {
      const http = useApp.getState().http
      try {
        const res = await http.post<{
          awarded: boolean
          platform: SocialPlatform
          points_awarded: number
          total_points: number
        }>('/social/claim', { platform })

        // Update claimed state regardless of awarded (idempotent)
        set({
          claimed: { ...get().claimed, [platform]: true },
        })

        return { awarded: res.data.awarded, totalPoints: res.data.total_points }
      } catch (error) {
        const errorMsg = extractErrorMessage(error)
        set({ error: errorMsg })
        console.error(errorMsg)
        return null
      }
    },

    clearError: () => set({ error: null }),

    getUrl: (platform: SocialPlatform) => SOCIAL_URLS[platform],
  }),
  false,
)

